import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import { getPdfConfiguration, updatePdfConfiguration } from './service';
import { BACK_KEYS } from '@/components/PdfConfiguration/constants';

function readPayloadValues(payload, storeKeys = Object.keys(BACK_KEYS)) {
  return storeKeys
    .filter((storeKey) => payload?.[BACK_KEYS[storeKey]] !== undefined)
    .map((storeKey) => [
      storeKey,
      payload[BACK_KEYS[storeKey]] ?? initialState[storeKey],
    ]);
}

function getSentStoreKeys(requestBody) {
  const sentBackKeys = Object.keys(requestBody ?? {});
  return Object.keys(BACK_KEYS).filter((storeKey) =>
    sentBackKeys.includes(BACK_KEYS[storeKey])
  );
}

export const PdfConfigurationSlice = createSlice({
  name: 'PdfConfiguration',
  initialState,
  reducers: {
    setPdfConfigurationValue(state, action) {
      const { key, value } = action.payload;
      state[key] = value;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPdfConfiguration.pending, (state) => {
      state.isLoading = true;
      state.hasLoadError = false;
      state.updateCountOnLoad = state.updateCount;
    });
    // A load may not include saves still in flight or confirmed after it was
    // sent: those fields keep the value from their own save.
    builder.addCase(getPdfConfiguration.fulfilled, (state, action) => {
      readPayloadValues(action.payload).forEach(([storeKey, value]) => {
        const isSaving =
          state.latestUpdateRequestIds[storeKey] in state.pendingUpdateNumbers;
        const isConfirmedAfterLoad =
          (state.confirmedUpdates[storeKey]?.number ?? 0) >
          state.updateCountOnLoad;
        if (isSaving || isConfirmedAfterLoad) return;

        state[storeKey] = value;
        state.persistedValues[storeKey] = value;
      });
      state.previewLocation =
        action.payload?.pdf_address_preview_location ?? null;
      state.isLoading = false;
    });
    builder.addCase(getPdfConfiguration.rejected, (state) => {
      state.isLoading = false;
      state.hasLoadError = true;
    });
    builder.addCase(updatePdfConfiguration.pending, (state, action) => {
      state.updateCount += 1;
      state.pendingUpdateNumbers[action.meta.requestId] = state.updateCount;
      getSentStoreKeys(action.meta.arg).forEach((storeKey) => {
        state.latestUpdateRequestIds[storeKey] = action.meta.requestId;
        delete state.rolledBackFields[storeKey];
      });
    });
    // The response carries every field: only the fields this request sent
    // are confirmed, and only if no newer update of the field was confirmed.
    // The visible value is replaced only if the user has not changed it
    // since: it still shows what the latest request sent, or the rollback of
    // a failed latest request.
    builder.addCase(updatePdfConfiguration.fulfilled, (state, action) => {
      const { arg, requestId } = action.meta;
      const number = state.pendingUpdateNumbers[requestId];
      delete state.pendingUpdateNumbers[requestId];
      if (number === undefined) return;
      readPayloadValues(action.payload, getSentStoreKeys(arg)).forEach(
        ([storeKey, value]) => {
          if (number < (state.confirmedUpdates[storeKey]?.number ?? 0)) {
            return;
          }
          state.confirmedUpdates[storeKey] = { number, requestId };

          const previousPersistedValue = state.persistedValues[storeKey];
          state.persistedValues[storeKey] = value;

          const isLatestRequest =
            state.latestUpdateRequestIds[storeKey] === requestId;
          const isUntouched = isLatestRequest
            ? state[storeKey] === arg[BACK_KEYS[storeKey]]
            : state.rolledBackFields[storeKey] &&
              state[storeKey] === previousPersistedValue;
          if (isUntouched) {
            state[storeKey] = value;
          }
        }
      );
    });
    // Only the latest request of a field rolls it back, to the last value
    // confirmed by the backend, never to an unsaved optimistic one. A value
    // the user changed after sending (the contact text) is kept.
    builder.addCase(updatePdfConfiguration.rejected, (state, action) => {
      const { arg, requestId } = action.meta;
      delete state.pendingUpdateNumbers[requestId];
      getSentStoreKeys(arg).forEach((storeKey) => {
        if (
          state.latestUpdateRequestIds[storeKey] !== requestId ||
          state[storeKey] !== arg[BACK_KEYS[storeKey]] ||
          !(storeKey in state.persistedValues)
        ) {
          return;
        }
        state[storeKey] = state.persistedValues[storeKey];
        state.rolledBackFields[storeKey] = true;
      });
    });
  },
});

export const { setPdfConfigurationValue } = PdfConfigurationSlice.actions;

export default PdfConfigurationSlice.reducer;
